import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { api, fetchDocument } from '../infrastructure/api'
import { Check, FileSpreadsheet, X } from 'lucide-react'
import type { Artifact, FleetStatus, RequestStatus } from '../domain/models'

export const fleetStatus: Record<FleetStatus, { label: string; tone: string }> = {
  working: { label: 'На объекте', tone: 'green' },
  base: { label: 'На базе', tone: 'blue' },
  maintenance: { label: 'На ТО', tone: 'amber' },
}

export const requestStatus: Record<RequestStatus, { label: string; tone: string }> = {
  cancelled: { label: 'Отменена', tone: '' },
  pending: { label: 'Ожидает назначения', tone: 'amber' },
  assigned: { label: 'Назначена', tone: 'blue' },
  working: { label: 'В работе', tone: 'violet' },
  completed: { label: 'Завершена', tone: 'green' },
}

export function Badge({ children, tone = '' }: { children: ReactNode; tone?: string }) {
  return <span className={`badge ${tone}`}>{children}</span>
}

export function PageHeading({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow: string
  title: string
  description: string
  actions?: ReactNode
}) {
  return (
    <header className="page-heading">
      <div>
        <span className="eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {actions && <div className="heading-actions">{actions}</div>}
    </header>
  )
}

export function Modal({
  title,
  subtitle,
  children,
  onClose,
  footer,
  small = false,
}: {
  title: string
  subtitle?: string
  children: ReactNode
  onClose: () => void
  footer?: ReactNode
  small?: boolean
}) {
  const ref = useRef<HTMLElement>(null)
  useEffect(() => {
    const previous = document.activeElement as HTMLElement
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const el = ref.current
    el?.focus()
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
      if (event.key === 'Tab' && el) {
        const nodes = [
          ...el.querySelectorAll<HTMLElement>('button, input, select, textarea, a[href]'),
        ].filter((n) => !n.hasAttribute('disabled'))
        const first = nodes[0]
        const last = nodes[nodes.length - 1]
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault()
          last?.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first?.focus()
        }
      }
    }
    el?.addEventListener('keydown', handler)
    return () => {
      el?.removeEventListener('keydown', handler)
      document.body.style.overflow = previousOverflow
      previous?.focus()
    }
  }, [onClose])
  return createPortal(
    <div
      className="overlay modal-layer"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(event) => event.currentTarget === event.target && onClose()}
    >
      <section ref={ref} tabIndex={-1} className={`modal ${small ? 'small' : ''}`}>
        <header className="modal-header">
          <div>
            <span className="eyebrow">Карточка</span>
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <button className="icon-button" aria-label="Закрыть" onClick={onClose}>
            <X className="icon" />
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer className="modal-footer">{footer}</footer>}
      </section>
    </div>,
    document.body,
  )
}

export function ArtifactThumbnail({ artifact, large = false }: { artifact: Artifact; large?: boolean }) {
  return (
    <div className={`artifact-preview ${large ? 'large' : ''}`}>
      <span>{artifact.extension}</span>
      <FileSpreadsheet className="icon" />
    </div>
  )
}

export function ArtifactViewer({ artifact }: { artifact: Artifact }) {
  const [url, setURL] = useState('')
  const [rows, setRows] = useState<string[][]>([])
  const [error, setError] = useState('')
  useEffect(() => {
    let active = true
    let blobURL = ''
    if (artifact.generated && artifact.generationStatus !== 'ready') {
      setURL('')
      setRows(artifact.rows ?? [])
      setError(artifact.generationStatus === 'error' ? 'PDF не сформирован. Повторите генерацию.' : '')
      return () => { active = false }
    }
    Promise.all([
      fetchDocument(artifact.id),
      api<{ rows: string[][] }>('/mechanization/documents/' + encodeURIComponent(artifact.id) + '/preview'),
    ])
      .then(([blob, preview]) => {
        blobURL = URL.createObjectURL(blob)
        if (active) {
          setURL(blobURL)
          setRows(preview.rows)
        } else URL.revokeObjectURL(blobURL)
      })
      .catch((e) => {
        if (active) setError(e.message)
      })
    return () => {
      active = false
      URL.revokeObjectURL(blobURL)
    }
  }, [artifact.id, artifact.generated, artifact.generationStatus, artifact.rows])
  const graphical = ['PDF', 'PNG', 'JPG', 'JPEG'].includes(artifact.extension)
  return (
    <div>
      <p>{artifact.name}</p>
      {artifact.generated && artifact.generationStatus === 'pending' && <p role="status">PDF формируется на сервере. Обновите список через несколько секунд.</p>}
      {error && <p role="alert">{error}</p>}
      {url && (
        <a className="button secondary" href={url} download={artifact.name}>
          Скачать оригинал
        </a>
      )}
      {graphical && url ? (
        artifact.extension === 'PDF' ? (
          <iframe title={artifact.name} src={url} className="document-frame" />
        ) : (
          <img className="document-image" src={url} alt={artifact.name} />
        )
      ) : (
        <div className="panel-body">
          <p>
            {!url && !error
              ? 'Загружаем оригинал…'
              : rows.length
                ? 'Текстовый предпросмотр: первые 200 строк первого листа / абзацев. Полная версия и форматирование доступны в оригинале.'
                : 'Предпросмотр для этого формата недоступен. Скачайте оригинал.'}
          </p>
          <div className="table-wrap">
            <table>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (
                      <td key={j}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export const shortName = (name: string) =>
  name
    .split(' ')
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
